import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Route, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { clearDkimKeys, listDkimKeys } from 'src/actions/dkimKeys';
import { clearSendingDomain, get as getDomain } from 'src/actions/sendingDomains';
import { list as listSubaccounts } from 'src/actions/subaccounts';
import { listTrackingDomains } from 'src/actions/trackingDomains';
import RedirectAndAlert from 'src/components/globalAlert/RedirectAndAlert';
import { ExternalLink, PageLink, SupportTicketLink } from 'src/components/links';
import { Loading } from 'src/components/loading/Loading';
import { Banner, Box, Button, Page, Tabs } from 'src/components/matchbox';
import { Bold, TranslatableText } from 'src/components/text';
import { resolveReadyFor, resolveStatus } from 'src/helpers/domains';
import { selectTrackingDomainCname, selectTrackingDomainCnameV2 } from 'src/selectors/account';
import { selectDomain } from 'src/selectors/sendingDomains';
import { hasSubaccounts } from 'src/selectors/subaccounts';
import { selectTrackingDomainsListHibana } from 'src/selectors/trackingDomains';
import styled from 'styled-components';
import DkimKeysTab from './components/DkimKeysTab';
import Domains from './components/index';
import { DETAILS_BASE_URL, EXTERNAL_LINKS } from './constants/index';

const StyledBox = styled(Box)`
  max-width: 600px;
`;
function DetailsPage(props) {
  const {
    history,
    sendingDomainsPending,
    trackingDomainListPending,
    sendingOrBounceDomain,
    getDomain,
    listTrackingDomains,
    listDkimKeys,
    clearDkimKeys,
    clearSendingDomain,
    trackingDomainList,
    sendingDomainsGetError,
    listSubaccounts,
    hasSubaccounts,
    subaccounts,
    trackingDomainCname,
    trackingDomainCnameV2,
    trackingDomainListError,
    dkimKeysCount
  } = props;
  const location = useLocation();
  const [isTracking] = useState(useRouteMatch(`${DETAILS_BASE_URL}/tracking`));
  const [warningBanner, toggleBanner] = useState(true);
  const [trackingDomainNotFound, settrackingDomainNotFound] = useState(false);
  const { id } = useParams();
  const domain = isTracking
    ? _.find(trackingDomainList, ['domain', id.toLowerCase()])
    : sendingOrBounceDomain;

  const settingsPath = `${DETAILS_BASE_URL}/sending-bounce/${id}`;
  const dkimKeysPath = `${DETAILS_BASE_URL}/sending-bounce/${id}/dkim-keys`;

  const tabs = [
    {
      content: 'Settings',
      as: PageLink,
      to: settingsPath
    },
    {
      content: 'DKIM Keys',
      as: PageLink,
      to: dkimKeysPath
    }
  ];

  const selectedTab = tabs.findIndex(({ to }) => location.pathname === to);

  useEffect(() => {
    if (!isTracking) {
      getDomain(id);
    }
    return () => {
      clearSendingDomain();
      clearDkimKeys();
    };
  }, [clearSendingDomain, clearDkimKeys, getDomain, isTracking, id]);

  useEffect(() => {
    if (!isTracking && domain) {
      listDkimKeys({ domain: id, subaccount: domain.subaccount_id });
    }
  }, [listDkimKeys, isTracking, id, domain]);
  useEffect(() => {
    listTrackingDomains().then((res) => {
      //this logic redirects to list page when the tracking domain is not found in the list
      if (isTracking && !Boolean(_.find(res, ['domain', id.toLowerCase()]))) {
        settrackingDomainNotFound(true);
      }
    });
  }, [history, isTracking, listTrackingDomains, id]);

  useEffect(() => {
    if (hasSubaccounts && subaccounts?.length === 0) {
      listSubaccounts();
    }
  }, [hasSubaccounts, listSubaccounts, subaccounts]);

  if (!isTracking && sendingDomainsGetError) {
    return (
      <RedirectAndAlert
        to="/domains/list/sending"
        alert={{ type: 'error', message: sendingDomainsGetError.message }}
      />
    );
  }

  if (isTracking && (trackingDomainListError || trackingDomainNotFound)) {
    return (
      <RedirectAndAlert
        to="/domains/list/tracking"
        alert={{ type: 'error', message: 'Resource could not be found' }}
      />
    );
  }

  if (sendingDomainsPending || trackingDomainListPending) {
    return <Loading />;
  }
  if (!domain) {
    return null;
  }

  const resolvedStatus = isTracking ? domain.resolvedStatus : resolveStatus(domain.status);
  const readyFor = isTracking ? {} : resolveReadyFor(domain.status);
  const displaySendingAndBounceSection = readyFor.dkim && readyFor.bounce;

  const handleAllDomains = () => {
    if (isTracking) return history.push('/domains/list/tracking');

    return history.push('/domains/list/sending');
  };

  const renderSettingsContent = () => (
    <>
      <Domains.DomainStatusSection domain={domain} id={id} isTracking={isTracking} />

      <Domains.SetupForSending
        domain={domain}
        dkimKeysCount={dkimKeysCount}
        isSectionVisible={
          resolvedStatus !== 'blocked' && !isTracking && !displaySendingAndBounceSection
        }
      />
      <Domains.SetupBounceDomainSection
        domain={domain}
        isSectionVisible={
          resolvedStatus !== 'blocked' && !isTracking && !displaySendingAndBounceSection
        }
      />
      <Domains.SendingAndBounceDomainSection
        domain={domain}
        dkimKeysCount={dkimKeysCount}
        isSectionVisible={
          resolvedStatus !== 'blocked' && !isTracking && displaySendingAndBounceSection
        }
      />

      <Domains.LinkTrackingDomainSection
        domain={domain}
        isSectionVisible={
          resolvedStatus !== 'blocked' && !isTracking && resolvedStatus !== 'unverified'
        }
      />

      <Domains.VerifyEmailSection
        domain={domain}
        isSectionVisible={resolvedStatus === 'unverified' && !isTracking}
      />

      <Domains.TrackingDnsSection domain={domain} isSectionVisible={isTracking} />

      <Domains.TrackingDomainTLSCertificateSection domain={domain} isSectionVisible={isTracking} />

      <Domains.DeleteDomainSection
        domain={domain}
        history={history}
        id={id}
        isTracking={isTracking}
      />
    </>
  );

  const renderBanners = () => (
    <>
      {resolvedStatus === 'unverified' && warningBanner && !isTracking && (
        <Banner
          status="warning"
          title="Unverified domains will be removed two weeks after being added."
          onDismiss={() => {
            toggleBanner(false);
          }}
          mb="500"
        >
          <TranslatableText>
            It can take 24 to 48 hours for the DNS records to propogate and verify the domain.
          </TranslatableText>
          <Banner.Actions>
            <ExternalLink to={EXTERNAL_LINKS.VERIFY_SENDING_DOMAIN_OWNERSHIP}>
              Domains Documentation
            </ExternalLink>
          </Banner.Actions>
        </Banner>
      )}
      {resolvedStatus === 'unverified' && warningBanner && isTracking && (
        <Banner
          status="warning"
          title="Unverified domains will be removed two weeks after being added."
          onDismiss={() => {
            toggleBanner(false);
          }}
          mb="500"
        >
          <TranslatableText>
            To verify a tracking domain, edit its DNS settings to <Bold>add a CNAME record</Bold>{' '}
            with the value of{' '}
            <strong>
              {domain?.uses_managed_certificate ? trackingDomainCnameV2 : trackingDomainCname}
            </strong>
            .
          </TranslatableText>
          <Banner.Actions>
            <ExternalLink to={EXTERNAL_LINKS.TRACKING_DOMAIN_DOCUMENTATION}>
              Domains Documentation
            </ExternalLink>
          </Banner.Actions>
        </Banner>
      )}

      {resolvedStatus === 'blocked' && (
        <Banner status="danger" title="This domain has been blocked by SparkPost" mb="500">
          <StyledBox>
            If your domain's status is "Blocked", it's generally because your domain is already in
            use by another SparkPost account, your domain has been previously blocked for sending
            abusive traffic through our service or another provider, or because one or more of our
            requirements are not met.
          </StyledBox>
          <Banner.Actions>
            <SupportTicketLink as={Button}>Create Support ticket</SupportTicketLink>
            <ExternalLink to={EXTERNAL_LINKS.BLOCKED_DOMAIN_DOCUMENTATION}>
              Domains Documentation
            </ExternalLink>
          </Banner.Actions>
        </Banner>
      )}
    </>
  );

  if (isTracking) {
    return (
      <Domains.Container>
        <Page
          title="Domain Details"
          breadcrumbAction={{
            content: 'All Domains',
            onClick: handleAllDomains
          }}
        >
          {/* eslint-disable-next-line local/restrict-translatable-text -- renders JSX components */}
          {renderBanners()}
          {/* eslint-disable-next-line local/restrict-translatable-text -- renders JSX components */}
          {renderSettingsContent()}
        </Page>
      </Domains.Container>
    );
  }

  return (
    <Domains.Container>
      <Page
        title={id}
        breadcrumbAction={{
          content: 'All Domains',
          onClick: handleAllDomains
        }}
      >
        {/* eslint-disable-next-line local/restrict-translatable-text -- renders JSX components */}
        {renderBanners()}

        <Box>
          <Box position="relative">
            <Tabs selected={selectedTab} tabs={tabs} />
            {selectedTab === 1 && (
              <Box position="absolute" top="-20px" right="0">
                <Button variant="primary" as={PageLink} to={`${dkimKeysPath}/create`}>
                  Add DKIM Key
                </Button>
              </Box>
            )}
          </Box>

          <Box paddingTop="400">
            <Route exact path={settingsPath} render={renderSettingsContent} />
            <Route
              path={dkimKeysPath}
              render={() => <DkimKeysTab domainId={id} domain={domain} />}
            />
          </Box>
        </Box>
      </Page>
    </Domains.Container>
  );
}

export default connect(
  (state) => ({
    trackingDomainList: selectTrackingDomainsListHibana(state),
    trackingDomainCname: selectTrackingDomainCname(state),
    trackingDomainCnameV2: selectTrackingDomainCnameV2(state),
    trackingDomainListError: state.trackingDomains.error,
    sendingDomainsPending: state.sendingDomains.getLoading,
    trackingDomainListPending: state.trackingDomains.listLoading,
    hasSubaccounts: hasSubaccounts(state),
    sendingDomainsGetError: state.sendingDomains.getError,
    sendingOrBounceDomain: selectDomain(state),
    subaccounts: state.subaccounts.list,
    dkimKeysCount: state.dkimKeys.list?.length || 0
  }),
  {
    getDomain,
    listTrackingDomains,
    listDkimKeys,
    clearDkimKeys,
    listSubaccounts,
    clearSendingDomain
  }
)(DetailsPage);
